<template>
  <v-hover v-slot="{ hover }">
    <v-card
      class="explore-card card-hover px-4 py-3 cursor-pointer no-background d-flex flex-column justify-space-between"
      data-testid="explore-entityCard"
      height="340"
      outlined
      :data-tourid="tourId"
      :ripple="false"
      :to="route"
      @click="handleClick"
    >
      <div class="d-flex flex-column">
        <div class="d-flex align-center mb-2">
          <div
            class="entity-type-icon mr-2 d-flex justify-center rounded-circle"
            :class="entityClasses.iconType"
          >
            <v-icon color="white" size="14">
              {{ ICONS_BY_ENTITY[data.entityType] }}
            </v-icon>
          </div>

          <div>
            <span class="font-size-12">{{ entityName }}</span>
            <div class="mt-n2">
              <span
                v-if="isChallengeEntity"
                class="font-size-10 blue-grey--text text--darken-1"
              >
                {{ formatDate(data.startAt, 'MMM D, YY') }}
              </span>
              <span
                class="font-size-10 blue-grey--text text--darken-1"
                v-else
                >{{ formatDate(data.createdAt, 'MMM D, YY') }}</span
              >
            </div>
          </div>
        </div>

        <div class="d-flex flex-column justify-space-between">
          <div v-if="hasPredictedFinancialImpact">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-chip
                  v-on="on"
                  small
                  color="green lighten-2"
                  class="predicted-impact-chip mb-2"
                  data-testid="explore-entityCard-predictedFinancialImpact"
                >
                  <v-icon color="green" size="14" class="mr-1">
                    mdi-cash-multiple
                  </v-icon>
                  <span class="caption predicted-impact-chip__value">
                    {{ formattedPredictedFinancialImpact }}
                  </span>
                </v-chip>
              </template>
              <span>{{ $t('rev.financial_potential_impact.chip_label') }}</span>
            </v-tooltip>
          </div>

          <div class="d-flex flex-column">
            <BackgroundImage
              v-if="shouldDisplayImage"
              data-private
              class="flex-grow-1 background-image-size rounded mb-1"
              data-testid="explore-entityCard-image"
              :size="IMAGE_SIZES.THUMBNAIL"
              :mediaObject="data.image"
              :defaultImage="null"
            >
              <div class="default-image d-flex align-start w-100 rounded"></div>
              <div class="status-container d-flex">
                <StatusChip showValidationIcons :entity="data" />
              </div>
            </BackgroundImage>

            <div v-if="shouldDisplayTags && hasTags" class="mt-3 d-flex">
              <v-chip
                class="chip-tags me-1"
                :color="hover ? 'blue-grey lighten-1' : 'blue-grey lighten-3'"
                small
              >
                <span
                  class="chip--name blue-grey--text text--darken-3 overflow-x-hidden"
                  :style="'text-overflow: ellipsis'"
                  >{{ firstTag }}</span
                >
              </v-chip>

              <v-tooltip v-if="hasExtraTags" top max-width="270px">
                <template v-slot:activator="{ on }">
                  <v-chip
                    v-on="on"
                    class="chip-tags me-2"
                    :color="
                      hover ? 'blue-grey lighten-1' : 'blue-grey lighten-3'
                    "
                    small
                  >
                    <span class="chip--name blue-grey--text text--darken-3">{{
                      tagsSize
                    }}</span>
                  </v-chip>
                </template>

                <ul class="px-2">
                  <li v-for="(tag, index) in extraTags" :key="index">
                    {{ tag.name }}
                  </li>
                </ul>
              </v-tooltip>
            </div>

            <div
              class="entity-title overflow-hidden mt-1"
              :class="shouldDisplayImage ? '' : 'mb-4'"
            >
              <v-chip
                small
                color="blue-grey lighten-3 mb-2 px-2"
                class="linked-entity"
                v-if="data.linkedEntityId"
                @click.stop="navigateTo(linkedEntityRoute)"
              >
                <v-icon
                  size="10"
                  class="me-1"
                  :color="ICON_COLORS_BY_ENTITY[data.linkedEntityType]"
                  >{{ ICONS_BY_ENTITY[data.linkedEntityType] }}</v-icon
                >
                <p
                  class="ma-0 pa-0 blue--text text-truncate linked-entity-name font-size-8"
                >
                  {{ linkedEntityText }}
                </p>
              </v-chip>

              <div class="d-flex justify-space-between mb-1">
                <text-highlight
                  class="body-2 mx-0 blue-grey--text text--darken-3 font-weight-500 entity-name"
                  data-testid="explore-entityCard-name"
                  highlightClass="yellow lighten-1"
                  highlightComponent="span"
                  :queries="highlightQueries"
                  :title="getEntityName"
                >
                  {{ getEntityName }}
                </text-highlight>

                <v-tooltip v-if="shouldShowHelper" top max-width="250px">
                  <template v-slot:activator="{ on }">
                    <v-icon class="ml-2" color="yellow" size="16px" v-on="on"
                      >mdi-information-outline
                    </v-icon>
                  </template>
                  <p class="body2 mb-0">
                    {{ $t('common.users.result_based_on_matches_by_fields') }}
                  </p>
                  <ul>
                    <li v-for="label in matchingFieldLabels" :key="label">
                      {{ label }}
                    </li>
                  </ul>
                </v-tooltip>
              </div>
            </div>

            <div v-if="isRoutineEntity && data.type" class="mt-3">
              <div>
                <v-icon color="blue-grey darken-1" class="pr-1" size="16"
                  >mdi-clipboard-text-outline</v-icon
                >
                <span class="caption blue-grey--text text--darken-1">{{
                  $t(`routines.type.${data.type}`)
                }}</span>
              </div>
            </div>

            <div>
              <StatusChip
                v-if="!shouldDisplayImage"
                class="mb-2"
                :entity="data"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-space-between align-end">
        <div class="d-flex flex-column">
          <p
            class="ma-0 pa-0 pb-1 caption blue-grey--text text--darken-1"
            style="font-size: 11px !important"
          >
            {{ createdOrOwnerBy }}
          </p>
          <ChipAvatar small :account="creatorOrOwnerAccount" />
        </div>

        <PriorityIcon
          v-if="data.priority"
          class="mb-1"
          :priority="data.priority"
        />
      </div>

      <SummaryActionable
        v-if="isAiSummarizeEnabled && isRevEntity && isCompletedStatus"
        :text="$t('ai_features.ai')"
        @click="$emit('openSummarizeDialog')"
      />
    </v-card>
  </v-hover>
</template>

<script>
import _ from 'lodash'
import { utils } from 'frontend-js-shared'
import { IMAGE_SIZES } from 'rever-media-sdk'
import { mapGetters } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import revMixin from '@/mixins/revMixin'
import moneyMixin from '@/mixins/moneyMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import access from '@/lib/access'

import BackgroundImage from '@/components/BackgroundImage/BackgroundImage.vue'
import StatusChip from '@/components/Core/StatusChip.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import PriorityIcon from '@/components/PriorityIcon.vue'
import SummaryActionable from '@/components/AiAssistant/SummaryActionable.vue'

const COLORS_BY_STATUS = {
  backlog: 'blue-grey-lighten-2',
  inProgress: 'blue',
  paused: 'yellow',
  completed: 'green',
  undu: 'blue-grey',
  learned: 'purple',
}

export default {
  name: 'EntityCard',

  mixins: [mixin, revMixin, trackerMixin, moneyMixin],

  components: {
    BackgroundImage,
    StatusChip,
    ChipAvatar,
    PriorityIcon,
    SummaryActionable,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
    matchingFields: {
      type: Array,
      default: () => [],
    },
    highlights: {
      type: Object,
      default: () => {},
    },
    searchValue: {
      type: String,
      default: '',
    },
    tourId: {
      type: String,
      default: '',
    },
    sort: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      IMAGE_SIZES,
      ICONS_BY_ENTITY: this.$constants.ICONS_BY_ENTITY,
      ICON_COLORS_BY_ENTITY: this.$constants.ICON_COLORS_BY_ENTITY,
      ENTITY_TYPES: this.$constants.exploreEntityTypes,
    }
  },

  methods: {
    formatDate(date, format) {
      return utils.dates.formatDate(date, format)
    },

    handleClick() {
      if (this.data.entityType === this.$constants.exploreEntityTypes.finding) {
        this.$emit('openFinding', this.data.id)
      }
      if (this.data.entityType === this.$constants.exploreEntityTypes.task) {
        eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE, this.data.id)
      }
    },
  },

  computed: {
    ...mapGetters(['account']),

    aiFeaturesAreEnabled() {
      return access.aiFeaturesAreEnabled(this.account, this.organization)
    },

    isAiSummarizeEnabled() {
      return (
        this.aiFeaturesAreEnabled &&
        this.$isFeatureEnabled(this.$featuresKeys.aiSummarize)
      )
    },

    linkedEntityText() {
      const customName =
        this.organization?.customNamings?.[this.data.linkedEntityType]
          ?.singular ?? null

      const entityName = this.$t(
        `common.${_.snakeCase(this.data.linkedEntityType)}_one`
      )

      return this.$t('tasks.link_to_entity', {
        entity: customName || entityName,
      })
    },

    linkedEntityRoute() {
      return this.generateRevRoute(
        this.data.organization,
        this.data.linkedEntityType,
        this.data.linkedEntityId
      )
    },

    matchingFieldsToShowInTooltip() {
      return this.matchingFields.filter(
        field => field !== 'name' && field !== 'situation'
      ) // 'name' and 'situation' are already displayed in the title and can be highlighted, thus they are not listed in the tooltip
    },

    shouldShowHelper() {
      return this.matchingFieldsToShowInTooltip.length > 0
    },

    matchingFieldLabels() {
      return this.matchingFieldsToShowInTooltip.map(field =>
        this.$t(`explore.fields.${field.replace('.', '_')}`)
      )
    },

    isSortedByPredictedFinancialImpact() {
      return this.sort?.startsWith('predictedFinancialImpactInUSD')
    },

    hasPredictedFinancialImpact() {
      return (
        this.isSortedByPredictedFinancialImpact &&
        this.isRevEntity &&
        _.isNumber(this.data.predictedFinancialImpactInUSD)
      )
    },

    formattedPredictedFinancialImpact() {
      return this.formatToUserCurrency(this.data.predictedFinancialImpactInUSD)
    },

    highlightQueries() {
      const titleHighlights =
        this.highlights?.situation || this.highlights?.name
      if (titleHighlights) return titleHighlights

      // if this.searchValue is undefined, the spread operator will be used on an empty array instead of undefined.
      // if we don't do this, using ...undefined will throw an erro
      return [this.searchValue, ...(this.searchValue?.split(' ') || [])]
    },

    getEntityName() {
      return this.data.name
    },

    isRevEntity() {
      const revs = new Set([
        this.ENTITY_TYPES.problemSolving,
        this.ENTITY_TYPES.quickAction,
        this.ENTITY_TYPES.improvementIdea,
      ])
      return revs.has(this.data.entityType)
    },
    shouldDisplayImage() {
      const entityWithImage = new Set([
        this.ENTITY_TYPES.problemSolving,
        this.ENTITY_TYPES.quickAction,
        this.ENTITY_TYPES.improvementIdea,
        this.ENTITY_TYPES.finding,
      ])
      return (
        entityWithImage.has(this.data.entityType) && Boolean(this.data.image)
      )
    },

    isEntityWithStatus() {
      const entityWithStatus = new Set([
        this.ENTITY_TYPES.problemSolving,
        this.ENTITY_TYPES.quickAction,
        this.ENTITY_TYPES.improvementIdea,
        this.ENTITY_TYPES.finding,
        this.ENTITY_TYPES.challenge,
      ])
      return entityWithStatus.has(this.data.entityType)
    },

    isCompletedStatus() {
      return this.data?.status === this.$constants.revStatus.completed
    },

    isRoutineEntity() {
      return this.data.entityType == this.ENTITY_TYPES.routine
    },

    isFindingEntity() {
      return this.data.entityType == this.ENTITY_TYPES.finding
    },

    isChallengeEntity() {
      return this.data.entityType == this.ENTITY_TYPES.challenge
    },

    creatorOrOwnerAccount() {
      const entityType = new Set([
        this.ENTITY_TYPES.problemSolving,
        this.ENTITY_TYPES.quickAction,
        this.ENTITY_TYPES.improvementIdea,
      ])
      return entityType.has(this.data?.entityType)
        ? this.data?.owner
        : this.data?.createdBy
    },

    entityClasses() {
      return {
        bgImage: this.shouldDisplayImage ? 'default-image' : '',
        iconType: `${this.ICON_COLORS_BY_ENTITY[this.data.entityType]}`,
        status: this.isEntityWithStatus
          ? `${COLORS_BY_STATUS[this.data.status]}`
          : '',
      }
    },

    createdOrOwnerBy() {
      const entityType = new Set([
        this.ENTITY_TYPES.problemSolving,
        this.ENTITY_TYPES.quickAction,
        this.ENTITY_TYPES.improvementIdea,
      ])
      return entityType.has(this.data.entityType)
        ? this.$t('common.owner')
        : this.$t('common.created_by')
    },

    shouldDisplayTags() {
      const entitiesWithTags = new Set([
        this.ENTITY_TYPES.problemSolving,
        this.ENTITY_TYPES.quickAction,
        this.ENTITY_TYPES.improvementIdea,
        this.ENTITY_TYPES.routine,
        this.ENTITY_TYPES.challenge,
        this.ENTITY_TYPES.task,
      ])
      return entitiesWithTags.has(this.data.entityType)
    },

    hasTags() {
      return Boolean(this.tags.length > 0)
    },

    hasExtraTags() {
      return Boolean(this.tags.length > 1)
    },

    tags() {
      return this.data?.tags || []
    },

    firstTag() {
      return this.tags[0]?.name
    },

    tagsSize() {
      return `+${this.tags.length - 1}`
    },

    extraTags() {
      return this.tags?.slice(1)
    },

    route() {
      const { entityType, id, organization } = this.data

      if (this.isRevEntity)
        return this.generateRevRoute(organization, entityType, id)

      switch (entityType) {
        case this.$constants.exploreEntityTypes.routine:
          return `/routine-executions/${id}`
        case this.$constants.exploreEntityTypes.challenge:
          return `/challenge/${organization}/${id}`
        default:
          return ''
      }
    },

    entityName() {
      const { entityType } = this.data
      switch (entityType) {
        case 'routine':
          return this.$t('routines.routine_one')
        case 'finding':
          return this.$t('routines.finding_one')
        case 'challenge':
          return this.$t('common.challenge_one')
        case 'task':
          return this.$t('common.task_one')
        default:
          return (
            this.organization?.customNamings?.[entityType]?.singular ||
            this.$t(`common.rev_type.${_.snakeCase(entityType)}_one`) ||
            ''
          )
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.explore-card {
  overflow: hidden;
}
.no-background::before {
  background: none !important;
}
.background-image-size {
  height: 112px;
  max-height: 112px;
}
.default-image {
  height: 112px;
}
.entity-type-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}
.status-container {
  bottom: 36px;
  left: 8px;
  position: relative;
}
.entity-title {
  max-height: 75px;
}

.entity-name {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.v-chip.chip-tags:before {
  background-color: inherit !important;
}
.chip-tags {
  height: 16px;
  &--name {
    font-size: 10px;
  }
}

.predicted-impact-chip {
  max-width: 100%;

  &__value {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.linked-entity-name:hover {
  text-decoration: underline $color-blue;
}

.card-hover:hover {
  .linked-entity {
    background-color: $color-blue-grey-lighten2 !important;
  }
}
</style>
